'use client';

import { ISalePageDetailV2Response } from '@/api/types';
import { useGachaUnusedTicketsQuery, useIsLoggedIn } from '@/entities';
import { useLoginInducingModal } from '@/hooks/useLoginInducingModal';
import { useModal } from '@/hooks/useModal';
import { qaAttr } from '@/lib/qa';
import webViewService from '@/lib/webViewService';
import { Column, Row } from '@vuddy/components';
import { getClientSideUserAgent, isIOSMobile } from '@vuddy/utils';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as toastStyles from '../../../../components/bottomSheet/physical/cartToast.css';
import { VerificationCodeModal } from '../../../../components/bottomSheet/physical/VerificationCodeModal';
import * as styles from './buttonGroup.css';
import { GachaButtonGroupProxyItem } from './ButtonGroupProxyItem';
import { GachaTicketButton } from './GachaTicketButton';
import { useHandleGachaOnSaleItem, useHandleGachaUnUsedTickets } from './hooks';
import { ScrollGachaButtonGroupOnSaleItems } from './ScrollGachaButtonGroupOnSaleItems';
import { getGachaCtaForProduct, getGachaCtaForStatus } from './selectGachaCta';

interface IGachaButtonGroupProps {
  salePage: ISalePageDetailV2Response;
  isPreview?: boolean;
  qaId?: string;
}

export const GachaButtonGroup = ({
  salePage,
  isPreview = false,
  qaId,
}: IGachaButtonGroupProps) => {
  const { confirm } = useModal();
  const { isLoggedIn } = useIsLoggedIn();
  const loginConfirm = useLoginInducingModal();
  const [isFirstShowUnusedTicketConfirm, setIsFirstShowUnusedTicketConfirm] =
    useState(true);

  // 코드 검증 상태
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [selectedProductIdForCode, setSelectedProductIdForCode] = useState<
    number | null
  >(null);
  // key: productId, value: 검증된 promotionCode
  const [verifiedProductCodes, setVerifiedProductCodes] = useState<
    Map<number, string>
  >(new Map());

  const { onClickOnSaleItem, products } = useHandleGachaOnSaleItem({
    salePage,
    isPreview,
  });
  const {
    data: unusedTicketsData,
    refetch,
    isFetching,
  } = useGachaUnusedTicketsQuery(salePage.gacha?.id!, {
    enabled: !isPreview && isLoggedIn,
  });
  const unusedTicketsCount = unusedTicketsData?.amount ?? 0;

  const { onClickUnUsedTicket } = useHandleGachaUnUsedTickets({
    userGachaTicketIds: unusedTicketsData?.userGachaTicketIds ?? [],
    amount: unusedTicketsCount,
    salePage,
  });

  const hasVerificationCode = (productId: number) =>
    products.find(p => p.id === productId)?.requiresPromotionCode ?? false;

  // 로그인 가드 + 코드 검증 래퍼 함수
  const handleOnSaleItemClick = (productId: number) => {
    if (!isLoggedIn) {
      loginConfirm();
      return;
    }

    // 코드 검증이 필요한 상품인데 아직 검증 안 된 경우
    if (
      hasVerificationCode(productId) &&
      !verifiedProductCodes.has(productId)
    ) {
      setSelectedProductIdForCode(productId);
      setVerificationModalOpen(true);
      return;
    }

    // 검증 완료된 경우 원래 로직 실행
    onClickOnSaleItem(productId, verifiedProductCodes.get(productId));
  };

  const refetchUnusedTicket = () => {
    if (document.visibilityState === 'visible') {
      refetch();

      // ios는 결제 후, 다시 가챠하러 돌아올 때, and, web과 다르게 기존 페이지를 그대로 노출함
      // 그래서 ios에서만 1초 후에 확인창을 띄움 > 2초로 변경
      const userAgent = getClientSideUserAgent();
      if (webViewService.isAppWebView(userAgent) && isIOSMobile(userAgent)) {
        setTimeout(() => setIsFirstShowUnusedTicketConfirm(true), 2000);
      }
    }
  };
  useEffect(() => {
    if (isLoggedIn) {
      document.addEventListener('visibilitychange', refetchUnusedTicket);
    } else {
      document.removeEventListener('visibilitychange', refetchUnusedTicket);
    }
    return () => {
      document.removeEventListener('visibilitychange', refetchUnusedTicket);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (
      isFirstShowUnusedTicketConfirm &&
      !isFetching &&
      unusedTicketsCount > 0
    ) {
      setIsFirstShowUnusedTicketConfirm(false);
      confirm({
        title: `보유 중인 가챠권이 ${unusedTicketsCount}개 있어요`,
        description:
          '가챠 진행을 누르면 보유 중인 가챠권으로 가챠를 시작합니다.',
        hasCancel: true,
        cancelText: '취소',
        confirmText: '가챠 진행',
        onConfirm: () => onClickUnUsedTicket(),
      });
    }
  }, [isFirstShowUnusedTicketConfirm, isFetching, unusedTicketsCount]);

  return (
    <Column
      className={styles.wrapper({ isPreview })}
      {...(qaId ? qaAttr(qaId) : {})}>
      <Column height={40} width="100%" className={styles.dimmed} />
      {unusedTicketsCount > 0 ? (
        <GachaButtonGroupProxyItem
          count={unusedTicketsCount}
          onClick={onClickUnUsedTicket}
        />
      ) : null}
      {salePage.saleStatus === 'ON_SALE' && products.length > 3 ? (
        <ScrollGachaButtonGroupOnSaleItems
          products={products}
          onClick={handleOnSaleItemClick}
        />
      ) : (
        <Row
          gap="gap_base"
          backgroundColor="dimHigh"
          paddingTop="padding_xs"
          paddingBottom="padding_xs"
          paddingX="padding_small"
          className={styles.buttonWrapper}>
          {salePage.saleStatus === 'ON_SALE' ? (
            products.map(product => (
              <GachaTicketButton
                key={product.id}
                {...getGachaCtaForProduct(product, handleOnSaleItemClick)}
              />
            ))
          ) : (
            <GachaTicketButton
              {...getGachaCtaForStatus(salePage.saleStatus, {
                startAt: salePage.saleTimeRange.startAt,
              })}
            />
          )}
        </Row>
      )}
      <VerificationCodeModal
        isOpen={verificationModalOpen}
        onClose={() => {
          setVerificationModalOpen(false);
          setSelectedProductIdForCode(null);
        }}
        onVerified={promotionCode => {
          toast.success('코드 검증이 완료되었습니다.', {
            className: toastStyles.verificationCodeToast,
          });
          if (selectedProductIdForCode !== null) {
            setVerifiedProductCodes(prev =>
              new Map(prev).set(selectedProductIdForCode, promotionCode)
            );
            // 검증 완료 후 원래 구매 로직 실행
            onClickOnSaleItem(selectedProductIdForCode, promotionCode);
          }
          setVerificationModalOpen(false);
          setSelectedProductIdForCode(null);
        }}
        productId={selectedProductIdForCode ?? 0}
        initialCode={
          selectedProductIdForCode !== null
            ? verifiedProductCodes.get(selectedProductIdForCode)
            : undefined
        }
      />
    </Column>
  );
};
